import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { TAGGED_DATA } from '@/constants/testIds';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { DownloadSimple, ShieldStar, Tag } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function TaggedData() {
    const [conceptKey, setConceptKey] = useState('all');
    const [patientId, setPatientId] = useState('all');

    const { data: concepts = [] } = useQuery({
        queryKey: ['admin-concepts', 'active-only'],
        queryFn: async () => (await api.get('/admin/concepts', { params: { include_archived: true } })).data,
    });

    const params = {};
    if (conceptKey && conceptKey !== 'all') params.concept_key = conceptKey;
    if (patientId && patientId !== 'all') params.patient_id = patientId;

    const { data: rows = [], isLoading } = useQuery({
        queryKey: ['admin-tagged-answers', conceptKey, patientId],
        queryFn: async () => (await api.get('/admin/tagged-answers', { params })).data,
    });

    const patients = useMemo(() => {
        const map = new Map();
        rows.forEach(r => { if (r.patient_id && !map.has(r.patient_id)) map.set(r.patient_id, r.patient_name || r.patient_id); });
        return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
    }, [rows]);

    const download = async () => {
        try {
            const res = await api.get('/admin/tagged-answers/export.csv', { params, responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
            const a = document.createElement('a');
            a.href = url; a.download = 'tagged-answers.csv';
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
            toast.success('CSV downloaded');
        } catch { toast.error('Export failed'); }
    };

    return (
        <div data-testid={TAGGED_DATA.root} className="px-4 sm:px-6 md:px-10 py-6 md:py-8 max-w-6xl">
            <header className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 flex items-center gap-2">
                        <ShieldStar size={12} weight="duotone" className="text-primary" /> Admin
                    </div>
                    <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl text-slate-900 mt-1">Tagged Data</h1>
                    <p className="text-slate-600 mt-2 max-w-2xl text-sm">
                        Every answer to a tagged check-in question, stored as structured data ready for export. Coordinator and family-member screens never surface any of this.
                    </p>
                </div>
                <Button data-testid={TAGGED_DATA.exportButton} onClick={download} className="rounded-full bg-primary text-primary-foreground min-h-[44px] sm:min-h-0 shrink-0">
                    <DownloadSimple size={16} className="mr-1" /> Export CSV
                </Button>
            </header>

            <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 mb-4">
                <div className="w-full sm:min-w-[220px] sm:w-auto">
                    <Select value={conceptKey} onValueChange={setConceptKey}>
                        <SelectTrigger data-testid={TAGGED_DATA.conceptFilter}><SelectValue placeholder="All concepts" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All concepts</SelectItem>
                            {concepts.map(c => (
                                <SelectItem key={c.key} value={c.key}>
                                    {c.display_name} <span className="text-slate-400">· {c.key}</span>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="w-full sm:min-w-[220px] sm:w-auto">
                    <Select value={patientId} onValueChange={setPatientId}>
                        <SelectTrigger data-testid={TAGGED_DATA.memberFilter}><SelectValue placeholder="All family members" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All family members</SelectItem>
                            {patients.map(p => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="text-xs text-slate-500 sm:ml-auto">{rows.length} record{rows.length === 1 ? '' : 's'}</div>
            </div>

            {isLoading ? (
                <div className="text-sm text-slate-500 py-10 text-center">Loading…</div>
            ) : rows.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-300 py-16 text-center text-sm text-slate-500">
                    <Tag size={28} className="mx-auto text-slate-400 mb-2" />
                    No tagged answers match your filters yet.
                </div>
            ) : (
                <div className="rounded-xl border border-slate-200 bg-card shadow-sm overflow-hidden">
                    <div className="hidden md:grid grid-cols-[150px_1fr_1fr_120px_100px_120px] gap-3 px-5 py-2 text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50/60 border-b border-slate-200">
                        <div>Date</div><div>Family member</div><div>Concept</div><div>Value</div><div>Unit</div><div>Source</div>
                    </div>
                    {rows.map(r => (
                        <div key={r.tagged_answer_id} data-testid={TAGGED_DATA.row} className="border-t border-slate-100 first:border-t-0">
                            {/* Desktop */}
                            <div className="hidden md:grid grid-cols-[150px_1fr_1fr_120px_100px_120px] gap-3 px-5 py-3 items-center text-sm">
                                <div className="text-slate-600 text-xs">{new Date(r.answered_at).toLocaleString()}</div>
                                <div className="text-slate-800">{r.patient_name || r.patient_id}</div>
                                <div>
                                    <div className="text-slate-800">{r.concept_display || r.concept_key}</div>
                                    <div className="text-[11px] font-mono text-slate-500">
                                        {r.concept_key}
                                        {r.code_system && r.code_system !== 'none' && r.code && <> · {r.code_system} {r.code}</>}
                                    </div>
                                </div>
                                <div className="font-mono text-slate-900">{String(r.value)}</div>
                                <div className="text-xs text-slate-600">{r.unit || '—'}</div>
                                <div className="text-xs text-slate-600">{r.source}</div>
                            </div>
                            {/* Mobile card */}
                            <div className="md:hidden px-4 py-3 space-y-1">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                        <div className="text-slate-800 font-medium text-sm">{r.concept_display || r.concept_key}</div>
                                        <div className="text-[11px] font-mono text-slate-500">{r.concept_key}{r.code_system && r.code_system !== 'none' && r.code ? ` · ${r.code_system} ${r.code}` : ''}</div>
                                    </div>
                                    <div className="font-mono text-slate-900 text-base shrink-0">{String(r.value)}<span className="text-slate-500 text-xs font-normal ml-1">{r.unit || ''}</span></div>
                                </div>
                                <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-2">
                                    <span>{r.patient_name || r.patient_id}</span>
                                    <span>·</span>
                                    <span>{new Date(r.answered_at).toLocaleString()}</span>
                                    <span>·</span>
                                    <span>{r.source}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
